
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useImpersonationActions = (
  setIsImpersonating: (value: boolean) => void,
  setOriginalUser: (user: User | null) => void,
  originalUser: User | null
) => {
  const impersonateUser = async (email: string): Promise<void> => {
    try {
      const currentUser = (await supabase.auth.getSession()).data.session?.user;
      
      if (!currentUser) {
        throw new Error("You must be logged in to impersonate a user");
      }
      
      if (!originalUser) {
        setOriginalUser(currentUser);
      }
      
      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { email }
      });
      
      if (error) {
        throw error;
      }
      
      if (!data || !data.signInLink) {
        throw new Error("No sign-in link returned");
      }
      
      localStorage.setItem('impersonation', JSON.stringify({
        isImpersonating: true,
        originalUser: currentUser
      }));
      
      setIsImpersonating(true);
      
      const newTab = window.open(data.signInLink, '_blank');
      
      if (!newTab) {
        throw new Error("Failed to open new tab for impersonation session");
      } else {
        toast.success(`Opened impersonation session for ${email} in a new tab`);
      }
    } catch (error: any) {
      console.error("Impersonation error:", error);
      toast.error(`Failed to impersonate user: ${error.message}`);
      throw error;
    }
  };

  const stopImpersonating = async (): Promise<void> => {
    try {
      if (!originalUser || !originalUser.id) {
        throw new Error("Original user information is missing");
      }
      
      const { data, error } = await supabase.functions.invoke('revert-impersonation', {
        body: { originalUserId: originalUser.id }
      });
      
      if (error) {
        throw error;
      }
      
      if (!data || !data.signInLink) {
        throw new Error("No sign-in link returned");
      }
      
      localStorage.removeItem('impersonation');
      setIsImpersonating(false);
      setOriginalUser(null);
      
      await supabase.auth.signOut();
      
      window.location.href = data.signInLink;
      
      toast.success("Returning to admin account");
    } catch (error: any) {
      console.error("Error stopping impersonation:", error);
      toast.error(`Failed to revert impersonation: ${error.message}`);
    }
  };

  return {
    impersonateUser,
    stopImpersonating
  };
};
